import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { migrarMonday, getOtConfig } from '../api/client'
import { EstadoOt } from '../components/ot/Etiquetas'

// Operaciones > Carga masiva > Órdenes de fabricación: the monday board
// "Estado Ordenes de Fabricación" (its Excel export) loaded as work orders.
// Simulate first (nothing is written), read the report, then apply. Applying
// twice creates nothing new (a folio that is already an OT is skipped).
// Takes the function "migrar_monday".
export default function PlantaMigracion() {
  const { t } = useTranslation(['operations', 'common'])
  const navigate = useNavigate()
  const [puede, setPuede] = useState(null)
  const [archivo, setArchivo] = useState(null)
  const [informe, setInforme] = useState(null)
  const [ocupado, setOcupado] = useState(false)
  const [error, setError] = useState('')
  const [filtro, setFiltro] = useState('')            // '' | estado | aviso:<code> | omitir
  const [buscar, setBuscar] = useState('')

  useEffect(() => { getOtConfig().then(c => setPuede(c.funciones.includes('migrar_monday'))).catch(() => setPuede(false)) }, [])

  const correr = async (aplicar) => {
    if (!archivo) return
    if (aplicar && !window.confirm(t('ot.migracion.confirmar', { count: informe?.totales?.crear || 0 }))) return
    setOcupado(true); setError('')
    try {
      setInforme(await migrarMonday(archivo, aplicar))
      setFiltro('')
    } catch (err) {
      const d = err.response?.data?.detail
      setError(d?.message || (typeof d === 'string' ? d : t('ot.migracion.fallo')))
    }
    setOcupado(false)
  }

  const filas = useMemo(() => {
    if (!informe) return []
    const b = buscar.trim().toLowerCase()
    return informe.filas.filter(p =>
      (!filtro || (filtro === 'omitir' ? p.accion === 'omitir'
        : filtro === 'existe' ? p.accion === 'existe'
        : filtro === 'difiere' ? p.accion === 'existe' && p.diferencias.length > 0
        : filtro.startsWith('aviso:') ? p.accion === 'crear' && p.avisos.includes(filtro.slice(6))
        : p.accion === 'crear' && p.estado === filtro)) &&
      (!b || [p.folio, p.codigo, p.descripcion, p.nombre, p.equipo, p.responsable_nombre].some(v => v && String(v).toLowerCase().includes(b))))
  }, [informe, filtro, buscar])

  if (puede === false) return <div className="text-sm text-gray-500">{t('ot.migracion.sinFuncion')}</div>
  const tot = informe?.totales
  const chip = (clave, etiqueta, n, tono) => (
    <button key={clave} type="button" onClick={() => setFiltro(f => (f === clave ? '' : clave))}
      className={`rounded-lg border px-3 py-2 text-left transition-colors ${filtro === clave ? 'border-gray-900 ring-1 ring-gray-900' : 'border-gray-200 hover:bg-gray-50'} ${tono || 'bg-white'}`}>
      <span className="block text-lg font-semibold tabular-nums text-gray-900">{n}</span>
      <span className="block text-[11px] text-gray-600">{etiqueta}</span>
    </button>
  )

  return (
    <div>

      <section className="rounded-xl border border-gray-200 bg-white p-4 mb-5 space-y-3">
        <ol className="text-sm text-gray-700 list-decimal pl-5 space-y-1">
          <li>{t('ot.migracion.paso1')}</li>
          <li>{t('ot.migracion.paso2')}</li>
          <li>{t('ot.migracion.paso3')}</li>
        </ol>
        <div className="flex flex-wrap items-center gap-3">
          <input type="file" accept=".xlsx" onChange={e => { setArchivo(e.target.files?.[0] || null); setInforme(null) }}
            className="text-sm" />
          <button type="button" disabled={!archivo || ocupado} onClick={() => correr(false)}
            className="bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-40">
            {ocupado && !informe ? t('ot.migracion.leyendo') : t('ot.migracion.simular')}
          </button>
          {informe && !informe.aplicado && tot.crear > 0 && (
            <button type="button" disabled={ocupado} onClick={() => correr(true)}
              className="bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-40">
              {t('ot.migracion.aplicar', { count: tot.crear })}
            </button>
          )}
          {informe?.aplicado && (
            <button type="button" onClick={() => navigate('/operations/planta/ot?vista=lista')}
              className="text-sm font-medium text-indigo-700 hover:underline">{t('ot.migracion.verLista')}</button>
          )}
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
        {informe && (
          <p className={`rounded-lg px-3 py-2 text-sm ${informe.aplicado ? 'bg-emerald-50 text-emerald-900' : 'bg-amber-50 text-amber-900'}`}>
            {informe.aplicado ? t('ot.migracion.aplicado', { count: tot.crear }) : t('ot.migracion.simulado', { count: tot.crear })}
          </p>
        )}
      </section>

      {tot && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-9 gap-2 mb-3">
            {chip('', t('ot.migracion.filas'), tot.filas)}
            {Object.entries(tot.por_estado).map(([e, n]) => chip(e, t(`ot.estados.${e}`, { defaultValue: e }), n))}
            {chip('existe', t('ot.migracion.existentes'), tot.existentes, 'bg-sky-50/60')}
            {tot.con_diferencias > 0 && chip('difiere', t('ot.migracion.conDiferencias'), tot.con_diferencias, 'bg-amber-50')}
            {chip('omitir', t('ot.migracion.omitidas'), tot.omitir, 'bg-gray-50')}
          </div>
          {Object.keys(tot.avisos).length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {Object.entries(tot.avisos).sort((a, b) => b[1] - a[1]).map(([a, n]) => (
                <button key={a} type="button" onClick={() => setFiltro(f => (f === `aviso:${a}` ? '' : `aviso:${a}`))}
                  title={t(`ot.migracion.avisosAyuda.${a}`, { defaultValue: '' })}
                  className={`rounded-full px-3 py-1 text-xs font-medium border ${filtro === `aviso:${a}` ? 'border-amber-600 bg-amber-100 text-amber-900' : 'border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100'}`}>
                  ⚠ {t(`ot.migracion.avisos.${a}`, { defaultValue: a })} · {n}
                </button>
              ))}
            </div>
          )}
          <div className="flex items-center gap-3 mb-2">
            <input value={buscar} onChange={e => setBuscar(e.target.value)} placeholder={t('ot.migracion.buscar')}
              className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm w-72 bg-white" />
            <span className="text-xs text-gray-500">{t('ot.migracion.mostrando', { n: filas.length, total: informe.filas.length })}</span>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
            <table className="text-sm min-w-full">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50 text-left text-gray-600">
                  {['fila', 'folio', 'codigo', 'descripcion', 'tipo', 'estado', 'equipo', 'responsable', 'prioridad', 'avisos'].map(c => (
                    <th key={c} className="px-3 py-2 font-semibold whitespace-nowrap">{t(`ot.migracion.col.${c}`)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filas.slice(0, 600).map(p => (
                  <tr key={p.fila} className={`border-b border-gray-50 ${p.accion === 'omitir' ? 'bg-gray-50 text-gray-400' : p.accion === 'existe' ? 'bg-sky-50/40' : ''}`}>
                    <td className="px-3 py-1.5 tabular-nums text-gray-400">{p.fila}</td>
                    <td className="px-3 py-1.5 font-mono">{p.folio || '—'}</td>
                    <td className="px-3 py-1.5 font-mono text-xs">{p.codigo || '—'}</td>
                    <td className="px-3 py-1.5 max-w-[320px] truncate" title={p.nombre}>{p.descripcion || p.nombre}</td>
                    <td className="px-3 py-1.5 text-xs">{p.tipo ? t(`ot.tipos.${p.tipo}`, { defaultValue: p.tipo }) : ''}</td>
                    <td className="px-3 py-1.5 whitespace-nowrap">
                      {p.accion === 'omitir'
                        ? <span className="text-xs">{t(`ot.migracion.omitida.${p.motivo}`, { defaultValue: p.motivo })}</span>
                        : p.accion === 'existe'
                          ? <span className="text-xs text-sky-900">{t('ot.migracion.yaEs', { ot: p.ot_codigo })}</span>
                          : <EstadoOt estado={p.estado} />}
                    </td>
                    <td className="px-3 py-1.5 text-xs">
                      {p.grupo_nombre ? <span className="font-semibold">{p.grupo_nombre}</span>
                        : p.proyecto_nombre || (p.equipo ? <span className="text-amber-700">{p.equipo}</span> : '—')}
                    </td>
                    <td className="px-3 py-1.5 text-xs">{p.responsable_nombre || '—'}</td>
                    <td className="px-3 py-1.5 text-xs">{p.prioridad ? t(`ot.prioridades.${p.prioridad}`) : '—'}</td>
                    <td className="px-3 py-1.5">
                      <span className="flex flex-wrap gap-1">
                        {p.accion === 'existe' && !p.diferencias.length && <span className="text-[10px] text-emerald-700">✓ {t('ot.migracion.coincide')}</span>}
                        {p.accion === 'existe' && p.diferencias.map(x => (
                          <span key={x.campo} title={t(`ot.migracion.notas.${x.nota}`, { defaultValue: '' })}
                            className={`rounded px-1.5 py-0.5 text-[10px] ${x.nota === 'rem_adelantada' ? 'bg-sky-100 text-sky-900' : 'bg-amber-100 text-amber-900'}`}>
                            {t(`ot.migracion.campos.${x.campo}`)}: {fmtValor(x.campo, x.rem, t)} → {fmtValor(x.campo, x.monday, t)}
                          </span>
                        ))}
                        {p.accion !== 'existe' && p.avisos.map(a => (
                          <span key={a} className="rounded bg-amber-50 px-1.5 py-0.5 text-[10px] text-amber-800">{t(`ot.migracion.avisos.${a}`, { defaultValue: a })}</span>
                        ))}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
}

// A compared value as people read it (states and priorities by name).
function fmtValor(campo, v, t) {
  if (v == null) return '—'
  if (campo === 'estado') return t(`ot.estados.${v}`, { defaultValue: v })
  if (campo === 'prioridad') return t(`ot.prioridades.${v}`, { defaultValue: v })
  return v
}
